import { Link } from 'react-router-dom';
import type { PublicProject } from '../../services/projectService';
import { formatAmount } from '../../utils/numberFormat';

/** Format a SAR amount that may arrive as a BigInt-serialized string or a number. */
export function formatSar(value: string | number | null | undefined, isRTL: boolean): string | null {
  if (value == null) return null;
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return null;
  return `${formatAmount(n)} ${isRTL ? 'ريال' : 'SAR'}`;
}

const STATUS_LABEL: Record<string, { en: string; ar: string }> = {
  PLANNING: { en: 'Planning', ar: 'تخطيط' },
  UNDER_CONSTRUCTION: { en: 'Under construction', ar: 'تحت الإنشاء' },
  READY: { en: 'Ready', ar: 'جاهز' },
  SOLD_OUT: { en: 'Sold out', ar: 'مكتمل البيع' },
};

/**
 * A licensed-project card for the investment hub. `variant='invest'` shows the
 * current-investors strip; `variant='contribute'` shows the developer-set
 * contribution range. Links to the existing project detail for full units.
 */
export default function ProjectInvestmentCard({ project, isRTL, variant }: { project: PublicProject; isRTL: boolean; variant: 'invest' | 'contribute' }) {
  const name = isRTL ? (project.nameAr || project.name) : project.name;
  const city = project.city || project.region || '';
  const status = project.status ? STATUS_LABEL[project.status] : null;
  const investors = project.currentInvestors || [];
  const cMin = formatSar(project.contributionMin, isRTL);
  const cMax = formatSar(project.contributionMax, isRTL);

  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-all hover:-translate-y-1 hover:border-[var(--pv-color-accent-soft)] hover:shadow-[0_14px_34px_-14px_rgba(0,77,64,0.28)]">
      {/* Cover */}
      <div className="relative h-32 bg-[#004D40] bg-cover bg-center" style={project.coverUrl ? { backgroundImage: `url(${project.coverUrl})` } : undefined}>
        {project.investmentStatus === 'OPEN_FOR_INVESTMENT' && (
          <span className="absolute top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-[#003930] shadow-sm" style={isRTL ? { right: '0.75rem' } : { left: '0.75rem' }}>
            <i className="ri-funds-line" aria-hidden="true" />{isRTL ? 'مفتوح للاستثمار' : 'Open for investment'}
          </span>
        )}
        {variant === 'contribute' && project.seekingContribution && (
          <span className="absolute top-3 inline-flex items-center gap-1 rounded-full bg-[#DFA906] px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm" style={isRTL ? { left: '0.75rem' } : { right: '0.75rem' }}>
            <i className="ri-checkbox-circle-fill" aria-hidden="true" />{isRTL ? 'يبحث عن تمويل' : 'Seeking funding'}
          </span>
        )}
      </div>

      <div className={`flex flex-1 flex-col p-4 ${isRTL ? 'text-right' : ''}`}>
        <h3 className="truncate text-[15px] font-semibold text-slate-900 group-hover:text-[#003930]">{name}</h3>
        <div className={`mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {city && <span className="flex items-center gap-1"><i className="ri-map-pin-2-line" />{city}</span>}
          {status && <span className="flex items-center gap-1"><i className="ri-building-3-line" />{isRTL ? status.ar : status.en}</span>}
          <span className="flex items-center gap-1"><i className="ri-home-4-line" />{project.unitsCount ?? 0} {isRTL ? 'وحدة' : 'units'}</span>
        </div>

        {/* Contribution range (contribute variant) */}
        {variant === 'contribute' && (cMin || cMax) && (
          <div className="mt-3 rounded-xl border border-[#FBE5A3] bg-[#FEF8E8] px-3 py-2">
            <div className="text-[11px] font-medium text-slate-500">{isRTL ? 'حصة المساهمة' : 'Contribution range'}</div>
            <div className="text-sm font-semibold text-[#8C6A0B]">
              {cMin && cMax ? `${cMin} - ${cMax}` : (cMin || cMax)}
            </div>
          </div>
        )}

        {/* Current investors (invest variant) */}
        {variant === 'invest' && (
          <div className="mt-3">
            <div className={`flex items-center justify-between text-[11px] font-medium text-slate-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span>{isRTL ? 'المستثمرون الحاليون' : 'Current investors'}</span>
              <span className="text-slate-400">{project.investorsCount ?? 0}</span>
            </div>
            {investors.length > 0 ? (
              <div className={`mt-1.5 flex flex-wrap gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {investors.slice(0, 4).map((inv) => (
                  <span key={inv.id} className="inline-flex items-center gap-1 rounded-full bg-[var(--pv-color-surface-sunken)] px-2 py-0.5 text-[11px] text-[#003930]">
                    <i className={inv.ownerType === 'company' ? 'ri-building-line' : 'ri-user-line'} aria-hidden="true" />
                    <span className="max-w-[8rem] truncate">{(isRTL ? inv.nameAr : inv.name) || inv.name || (isRTL ? 'مستثمر' : 'Investor')}</span>
                  </span>
                ))}
                {(project.investorsCount ?? 0) > 4 && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">+{(project.investorsCount ?? 0) - 4}</span>}
              </div>
            ) : (
              <p className="mt-1 text-[11px] text-slate-400">{isRTL ? 'كن أول المستثمرين' : 'Be the first to invest'}</p>
            )}
          </div>
        )}

        <Link
          to={`/developers/project/${project.slug}`}
          className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#004D40] px-3 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
        >
          <i className="ri-eye-line text-base" aria-hidden="true" />
          {isRTL ? 'عرض المشروع' : 'View project'}
        </Link>
      </div>
    </div>
  );
}
